
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import ProfileSettings from "@/components/profile/ProfileSettings";
import MyCoursesList from "@/components/profile/MyCoursesList";
import PurchaseHistory from "@/components/profile/PurchaseHistory";
import Hero from "@/components/Hero";
import { Loader2 } from "lucide-react";

const UserProfile = () => {
  const { user, profile, loading, error } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabParam || "profile");

  // Set active tab based on URL query parameter
  useEffect(() => {
    if (tabParam && ["profile", "courses", "purchases"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  // Redirect to auth page if not logged in
  if (!loading && !user) {
    navigate("/auth");
    return null;
  }

  // Display username or email from user object directly if profile is not available
  const displayName = profile?.full_name || user?.email || "Kasutaja";

  return (
    <div className="fade-in">
      <Hero
        title="Minu konto"
        subtitle="Halda oma profiili ja kursuseid"
        buttonText="Alusta muutust"
        buttonLink="/contact"
        bgColor="bg-gradient-to-r from-kaija-peach to-kaija-light"
      />

      <section className="section-padding bg-white">
        <div className="container-custom max-w-5xl">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-kaija-accent" />
            </div>
          ) : (
            <>
              <div className="mb-8 text-center md:text-left">
                <h1 className="text-2xl font-bold mb-2 text-kaija-slate">
                  Tere, {displayName}
                </h1>
                <p className="text-gray-600">
                  Siit leiad oma konto seaded ja kursused
                </p>
                {error && (
                  <p className="text-red-500 text-sm mt-1">
                    Profiili laadimisel tekkis viga. Mõned funktsioonid võivad olla piiratud.
                  </p>
                )}
              </div>

              <Tabs
                defaultValue="profile"
                value={activeTab}
                onValueChange={(value) => {
                  setActiveTab(value);
                  navigate(`/profile?tab=${value}`);
                }}
                className="w-full"
              >
                <TabsList className="w-full mb-8 grid grid-cols-3 bg-gray-100/80 p-1 rounded-xl">
                  <TabsTrigger 
                    value="profile"
                    className="data-[state=active]:bg-white data-[state=active]:text-kaija-slate data-[state=active]:shadow-md transition-all duration-200"
                  >
                    Profiil
                  </TabsTrigger>
                  <TabsTrigger 
                    value="courses"
                    className="data-[state=active]:bg-white data-[state=active]:text-kaija-slate data-[state=active]:shadow-md transition-all duration-200"
                  >
                    Minu kursused
                  </TabsTrigger>
                  <TabsTrigger 
                    value="purchases"
                    className="data-[state=active]:bg-white data-[state=active]:text-kaija-slate data-[state=active]:shadow-md transition-all duration-200"
                  >
                    Ostud
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="profile" className="mt-0">
                  <ProfileSettings user={user} profile={profile} />
                </TabsContent>

                <TabsContent value="courses" className="mt-0">
                  <MyCoursesList userId={user?.id} />
                </TabsContent>

                <TabsContent value="purchases" className="mt-0">
                  <PurchaseHistory userId={user?.id} />
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default UserProfile;
